import { addMonths, DateOnly } from '@if/core/date'
import { EPaymentMethod } from '@src/types/constants'
import { ErrorAlert } from '@if/mui'
import { CircularProgress, Stack, styled } from '@mui/material'
import { DateCalendar } from '@mui/x-date-pickers'
import { useComputeCatchUpAmountOfOverdueForDays } from '@src/api/loan/loan-api'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { useUser } from '@src/providers/UserProvider'
import { useUnmountableDialog } from '@src/services/dialog'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { nanoid } from 'nanoid'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetCurrentOverdueAmount } from '../../LoanCalendar-hooks'
import EditPaymentCloseAllBtn from '../EditPaymentCloseAllBtn'
import { EditPaymentSchemaType } from '../EditPayments-schema'
import CatchUpAmountOfOverdueForDateDaySlot, {
  CatchUpAmountOfOverdueForDateDaySlotProps,
  DAY_WIDTH_PX,
} from './CatchUpAmountOfOverdueForDateDaySlot'

interface CatchUpAmountOfOverdueForDateDialogProps {
  simulation: SignedCalendarSimulation
  onConfirm: (data: EditPaymentSchemaType) => void
  onCloseAll: () => void
  onClose: () => void
}

const StyledDateCalendar = styled(DateCalendar)({
  height: '515px',
  maxHeight: '515px',
  width: '100%',
  '.MuiPickersSlideTransition-root, .MuiDayCalendar-loadingContainer': {
    minHeight: '430px',
  },
  '.MuiDayCalendar-weekDayLabel': {
    width: DAY_WIDTH_PX,
  },
  '.MuiPickersCalendarHeader-root': {
    marginTop: 0,
  },
})

const CatchUpAmountOfOverdueForDateDialog = ({
  simulation,
  onClose,
  onCloseAll,
  onConfirm,
}: CatchUpAmountOfOverdueForDateDialogProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  const hasCurrentOverdueAmount = useGetCurrentOverdueAmount(simulation.simulation)

  const today = useMemo(() => DateOnly.today(), [])
  const minDisplayDate = useMemo(() => {
    return today.toDate()
  }, [today])
  const loanActivationDate = simulation.simulation.fullLoan.loan.activationOn
  const minDate = useMemo(
    () =>
      SafePaymentActionDateService.getInstance()
        .getMinPaymentDate(EPaymentMethod.PreAuthorizedPayment, loanActivationDate, canBypassAcpLock)
        .toDate(),
    [canBypassAcpLock, loanActivationDate],
  )
  const shouldDisableDate = useCallback(
    (day: Date) => {
      return day.getTime() < minDate.getTime()
    },
    [minDate],
  )

  const maxDateOnly = useMemo(() => {
    const maxInterval = addMonths(today, 2)
    const minMaxDate =
      maxInterval.getTime() < simulation.simulation.fullLoan.loan.lastPaymentOn.getTime()
        ? maxInterval
        : simulation.simulation.fullLoan.loan.lastPaymentOn
    return minMaxDate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulation.simulation.fullLoan.loan.lastPaymentOn])

  const maxDate = useMemo(() => maxDateOnly.toDate(), [maxDateOnly])

  const {
    data: lateAmountForDays,
    isPending,
    error: errorApi,
  } = useComputeCatchUpAmountOfOverdueForDays(simulation.simulation, today, maxDateOnly)
  const { dialogProps, closeDialog } = useUnmountableDialog(onClose, isPending)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)

  const handleConfirm = () => {
    if (selectedDate && lateAmountForDays) {
      const effectiveDate = DateOnly.fromDate(selectedDate)
      const amount = lateAmountForDays.get(effectiveDate.toString())
      if (amount) {
        onConfirm({
          id: nanoid(),
          amount,
          effectiveDate,
          paymentMethod: EPaymentMethod.PreAuthorizedPayment,
          sendSmsReminder: true,
          note: null,
          isNew: true,
          isDeleted: false,
          isLocked: false,
        })
        closeDialog()
      }
    }
  }

  const error = useMemo(() => {
    if (!isPending && (!lateAmountForDays || lateAmountForDays.size === 0))
      return t('loan.payment.catchUpAmountOfOverdueForDate.errors.noValidDatesFound')
    if (!hasCurrentOverdueAmount) return t('loan.payment.catchUpAmountOfOverdueForDate.errors.simulationNotValid')
    return errorApi
  }, [errorApi, isPending, lateAmountForDays, hasCurrentOverdueAmount, t])

  return (
    <FormDialog
      {...dialogProps}
      loading={isPending}
      disabled={selectedDate === null}
      title={t('loan.payment.catchUpAmountOfOverdueForDate.title')}
      onConfirm={handleConfirm}
      confirmLabel={t('loan.payment.catchUpAmountOfOverdueForDate.confirm')}
      additionalActions={<EditPaymentCloseAllBtn onCloseAll={onCloseAll} />}
    >
      {error && <ErrorAlert error={error} />}
      <Stack spacing={1}>
        <StyledDateCalendar
          onChange={setSelectedDate}
          loading={isPending}
          minDate={minDisplayDate}
          maxDate={maxDate}
          shouldDisableDate={shouldDisableDate}
          disabled={error !== null}
          renderLoading={() => <CircularProgress />}
          showDaysOutsideCurrentMonth
          slots={{
            day: CatchUpAmountOfOverdueForDateDaySlot as never,
          }}
          slotProps={{
            day: {
              lateAmountForDays: lateAmountForDays,
            } satisfies CatchUpAmountOfOverdueForDateDaySlotProps as never,
          }}
        />
      </Stack>
    </FormDialog>
  )
}

export default CatchUpAmountOfOverdueForDateDialog
